import {
  Box,
  formattedCurrency,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  Card,
  MergedScheduledPayment,
  ScheduledPaymentUpcomingTransaction,
  ScheduledPaymentCompletedTransaction,
  TableProps,
  removeWhiteSpaceURI,
} from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import moment from 'moment';
import { Table } from 'UI';
import TransactionSearchForm from './TransactionSearchForm';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useEditUpcomingPayment from '@/hooks/useApi/useEditUpcomingPayment';
import useCancelUpcomingPayment from '@/hooks/useApi/useCancelUpcomingPayment';
import useLegacyScheduledPayment from './useLegacyScheduledPayment';
import styles from '../../ScheduledTransactions.module.scss';
import { usePermissions } from '@/providers/Permissions.provider';
import { EditAmountDialogueHandler, DialogueHandlerProps } from '../../../Dialogue/types';

import EditAmountDialogue from '../../../Dialogue/EditAmount/EditAmountDialogue';
import EditAmountContext, { EditAmountCtx } from '../../../Dialogue/EditAmount/EditAmountContext';
import EditDateDialogue from '../../../Dialogue/EditDate/EditDateDialogue';
import { useTranslation } from 'react-i18next';
import { Button, TransactionStatus, TableFiltersContainer } from 'UI';

type TransactionsHistoryProps = {
  setNewStatus: (e: boolean) => void;
  transaction: MergedScheduledPayment;
};

type ScheduledPaymentTransaction = ScheduledPaymentUpcomingTransaction | ScheduledPaymentCompletedTransaction;

type FC = FunctionComponent<TransactionsHistoryProps>;

const TransactionsHistory: FC = (props: TransactionsHistoryProps) => {
  const { response } = useClientData();
  const { permissions } = usePermissions();
  const { transaction, setNewStatus } = props;
  const [status, setStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [transactionsList, setTransactionsList] = useState<ScheduledPaymentTransaction[]>([]);
  const [filteredLists, setFilteredLists] = useState<ScheduledPaymentTransaction[]>([]);
  const [startDate, setStartDate] = useState('');
  const [editAmount, setEditAmount] = useState('');
  // The /scheduled-payment/upcoming-payment/* endpoints key off the detail row
  // rather than the schedule ID + payment date the legacy EFT routes use.
  const [editDetailsId, setEditDetailsId] = useState('');

  const { editing: newEditing, editUpcomingPayment } = useEditUpcomingPayment();
  const { cancelling: newCancelling, cancelUpcomingPayment } = useCancelUpcomingPayment();
  const {
    editing: legacyEditing,
    cancelling: legacyCancelling,
    editLegacyScheduledPayment,
    cancelLegacyScheduledPayment,
  } = useLegacyScheduledPayment();
  const [shouldOpenEditAmountModal, setShouldOpenEditAmountModal] = useState(false);

  const [shouldOpenEditDateModal, setShouldOpenEditDateModal] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details.History' });
  const { t: tDetails } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();
  const toast = useToast();

  const scheduleType = transaction.PaymentType === 'debit' ? 'fund' : 'withdraw';
  const isEditableRail = !transaction.TransactionType?.toLowerCase().includes('credit');
  const isRowActionPending = newEditing || newCancelling || legacyEditing || legacyCancelling;

  const EditAmount: EditAmountCtx = {
    shouldOpenEditAmountModal,
  };

  const handleStatusChange = (statusVal: string) => {
    setStatus(statusVal);
  };

  const handleSearchTermChange = (searchTermVal: string) => {
    setSearchTerm(searchTermVal);
  };

  const handlers = {
    handleEditAmountDialogue: (handlerProps: DialogueHandlerProps<EditAmountDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: EditAmountDialogueHandler = {
        confirm: {
          success: {
            default: () => {
              if (editAmount !== '' && Number(editAmount) > 0) {
                setShouldOpenEditAmountModal(false);
                submitEditUpcomingPayment(editAmount, '');
              }
            },
          },
          fail: {
            default: () => {
              setShouldOpenEditAmountModal(false);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  // create lists
  useEffect(() => {
    if (!transaction) return;

    const merged: ScheduledPaymentTransaction[] = [
      ...transaction.UpcomingTransactions,
      ...transaction.CompletedTransactions,
    ].sort((a, b) => (a.ScheduledDate < b.ScheduledDate ? -1 : 1));

    setTransactionsList(merged);
  }, [transaction]);

  const search = () => {
    const keys: (keyof ScheduledPaymentTransaction)[] = ['Amount', 'ID'];

    setFilteredLists(
      transactionsList.filter(
        (data) =>
          (status === '' || status === 'all' || data.Status === status) &&
          (searchTerm === '' || keys.some((key) => data[key]?.toString().toLowerCase().includes(searchTerm))),
      ),
    );

    setFiltersApplied(!!status || !!searchTerm);
  };

  // search form
  useEffect(() => {
    search();
  }, [transactionsList]);

  // Edits a single upcoming payment's amount and/or date. `date` is passed
  // explicitly (rather than read from `newDate` state) so a date just set in the
  // same event as this call is never stale — see EditDateDialogue's onChange.
  async function submitEditUpcomingPayment(amount: string, date: string) {
    try {
      if (transaction.IsLegacy) {
        await editLegacyScheduledPayment(scheduleType, transaction.ID, startDate, { amount, date });
      } else {
        await editUpcomingPayment(editDetailsId, { amount: Number(amount), date });
      }

      setNewStatus(true);

      if (date) {
        toast.success(t('scheduleDateUpdatedToast', { defaultValue: 'Schedule Date Updated' }));
      }
    } catch {
      alertDialog.error(
        t('Dialogues.AmountEditError.title', { defaultValue: 'Error! The amount was not edited' }),
        tGlobal('genericError', { defaultValue: 'Something went wrong' }),
      );
    }
  }

  // scheduledDate is passed explicitly rather than read from `startDate` state,
  // since the Cancel button sets nothing beforehand — it's a single-click action.
  async function submitCancelUpcomingPayment(id: string, scheduledDate: string) {
    try {
      if (transaction.IsLegacy) {
        await cancelLegacyScheduledPayment(scheduleType, transaction.ID, scheduledDate);
      } else {
        await cancelUpcomingPayment(id);
      }

      setNewStatus(true);
    } catch {
      alertDialog.error(
        t('Dialogues.UpcomingTransactionCancelError.title', {
          defaultValue: 'Error! The transaction was not canceled',
        }),
        tGlobal('genericError', { defaultValue: 'Something went wrong' }),
      );
    }
  }

  const tableProps: TableProps<ScheduledPaymentTransaction> = {
    id: 'transactionHistoryTable',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'scheduledTransactionID',
        header: t('Table.scheduledTransactionID'),
        align: 'left',
        field: 'ID',
      },
      {
        id: 'transactionDate',
        header: t('Table.transactionDate'),
        align: 'left',
        field: 'ScheduledDate',
      },
      {
        id: 'transactionId',
        header: t('Table.transactionId'),
        align: 'left',
        template: ({ data }) => {
          return (
            <Table.Link
              to={`/transactions-history/details/${removeWhiteSpaceURI(scheduleType)}/${data.TransactionID}/${
                transaction.Currency
              }`}
            >
              {data.TransactionID}
            </Table.Link>
          );
        },
      },
      {
        id: 'transactionType',
        header: t('Table.transactionType'),
        align: 'left',
        template: () =>
          tDetails(`ScheduleDetailsSection.Rows.Type.Options.${scheduleType}`, {
            defaultValue: capitalizeFirstLetterOfWords(scheduleType),
          }),
      },
      {
        id: 'amount',
        header: t('Table.amount'),
        align: 'left',
        template: ({ data }) => `$ ${formattedCurrency(data.Amount)} ${response.Country === 'CA' ? 'CAD' : 'USD'} `,
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status || '')}
              label={capitalizeFirstLetterOfWords(
                t(`SearchForm.Status.Options.${data.Status}`, { defaultValue: data.Status }),
              )}
            />
          </Box>
        ),
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {data.Status === 'upcoming' &&
              response.Country === 'CA' &&
              isEditableRail &&
              permissions.ManageTransactions && (
                <>
                  <Button
                    id={`edit-date-${data.ID}`}
                    size="small"
                    disabled={isRowActionPending}
                    onClick={() => {
                      setEditAmount(data.Amount);
                      setShouldOpenEditDateModal(true);
                      setStartDate(data.ScheduledDate);
                      setEditDetailsId(data.ID);
                    }}
                  >
                    {t('Table.Rows.editDateButton')}
                  </Button>

                  <Button
                    id={`edit-amount-${data.ID}`}
                    size="small"
                    disabled={isRowActionPending}
                    onClick={() => {
                      setEditAmount(data.Amount);
                      setShouldOpenEditAmountModal(true);
                      setStartDate(data.ScheduledDate);
                      setEditDetailsId(data.ID);
                    }}
                  >
                    {t('Table.Rows.editAmountButton')}
                  </Button>

                  <Button
                    id={`cancel-${data.ID}`}
                    size="small"
                    disabled={isRowActionPending}
                    onClick={() => {
                      submitCancelUpcomingPayment(data.ID, data.ScheduledDate);
                    }}
                  >
                    {t('Table.Rows.cancelButton')}
                  </Button>
                </>
              )}
          </Box>
        ),
      },
    ],
    rows: filteredLists.map((transaction, index) => ({
      id: String(transaction.ID || index),
      data: transaction,
    })),
  };

  return (
    <EditAmountContext.Provider value={EditAmount}>
      <Box flx="col" gapSize="36px">
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Box flx="col" gap="sm">
            <TableFiltersContainer filtersApplied={filtersApplied}>
              <TableFiltersContainer.Trigger />

              <TableFiltersContainer.Content>
                <TransactionSearchForm
                  status={status}
                  searchTerm={searchTerm}
                  handleStatusChange={handleStatusChange}
                  handleSearchTermChange={handleSearchTermChange}
                  search={search}
                />
              </TableFiltersContainer.Content>
            </TableFiltersContainer>
          </Box>

          <Box>
            <div className={styles.tableContainer}>
              <Table {...tableProps} />
            </div>
          </Box>
        </Card>
      </Box>

      <div className="hiddenFormsPrint">
        <EditAmountDialogue
          setEditAmount={setEditAmount}
          handleDialogue={handlers.handleEditAmountDialogue}
          initalAmount={editAmount}
        />

        <EditDateDialogue
          visible={shouldOpenEditDateModal}
          onChange={(newDateVal) => {
            setShouldOpenEditDateModal(false);
            submitEditUpcomingPayment(editAmount, newDateVal);
          }}
          initalDate={moment(startDate).valueOf().toString()}
          onClose={() => {
            setShouldOpenEditDateModal(false);
          }}
        />
      </div>
    </EditAmountContext.Provider>
  );
};

export default TransactionsHistory;
